/**
 * Foamera Fabric Library — Storefront Fabric Browser Page
 *
 * Hydrates the Foamera fabric catalog section (~1013 fabrics). Ported from
 * extensions/theme-ext/assets/cfg-fabric.js — same filter logic, shapes, color
 * palette, and taxonomy maps. Rewritten for a standalone library page with:
 *  - Collection-grouped default view + flat mode when filters/search active
 *  - Color, Usage, Material, Feature filters (pattern filter skipped for v1)
 *  - Mobile filter sheet + desktop dropdowns + active-filter pills
 *  - Detail drawer + Favorites drawer (localStorage-backed)
 *  - Deep-link support via `#fabric=CODE`
 *
 * Namespace: window.PoufudicFabrics
 * Config:    window.__PFAB_CONFIG__  { appProxy, builderUrl }
 * ES5 compatible. No deps.
 */
(function() {
  'use strict';

  /* ══ CONSTANTS & CONFIG ══ */

  var CONFIG = window.__PFAB_CONFIG__ || {};
  var APP_PROXY = CONFIG.appProxy || '/apps/cushion-cfg';
  var BUILDER_URL = CONFIG.builderUrl || '/collections/all';
  var FAV_STORAGE_KEY = 'poufudic_fabric_favorites';
  var SEARCH_DEBOUNCE_MS = 200;

  /**
   * Shopify image CDN transform — returns a resized URL.
   * Cuts bandwidth 85-90% for mosaic tiles + card thumbs.
   *   toImg(url, 300)  → appends &width=300
   * Preserves original ?v= cache-buster.
   */
  function toImg(url, width) {
    if (!url || !width) return url || '';
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    // Strip any existing width/height params first to avoid dup
    var clean = url.replace(/([?&])(width|height|crop)=[^&]+/g, '$1').replace(/&$/,'').replace(/\?$/,'');
    var sep = clean.indexOf('?') > -1 ? '&' : '?';
    return clean + sep + 'width=' + width;
  }
  function srcset(url, widths) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return '';
    var parts = [];
    for (var i = 0; i < widths.length; i++) parts.push(toImg(url, widths[i]) + ' ' + widths[i] + 'w');
    return parts.join(', ');
  }

  /**
   * Lazy-load kicker. Chrome's native loading="lazy" is unreliable when we
   * swap large innerHTML blocks with ~150 images — browser skips kicking off
   * loads for images that are already near/in the viewport at swap time. We
   * observe each lazy img and, when it enters a 400px margin around the
   * viewport, promote it to eager and reassign src so the fetch actually
   * starts. Unobserves after firing. No-ops on browsers without IO.
   */
  var _pfabLazyObs = null;
  function kickLazyImages(container) {
    if (!container) return;
    var imgs = container.querySelectorAll('img[loading="lazy"]');
    if (!imgs.length) return;
    if (!('IntersectionObserver' in window)) {
      for (var j = 0; j < imgs.length; j++) imgs[j].removeAttribute('loading');
      return;
    }
    if (!_pfabLazyObs) {
      _pfabLazyObs = new IntersectionObserver(function(entries, obs) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) continue;
          var img = entries[k].target;
          img.removeAttribute('loading');
          if (!img.complete || img.naturalWidth === 0) {
            var s = img.getAttribute('src');
            if (s) { img.removeAttribute('src'); img.setAttribute('src', s); }
          }
          obs.unobserve(img);
        }
      }, { rootMargin: '400px 0px', threshold: 0.01 });
    }
    for (var i = 0; i < imgs.length; i++) _pfabLazyObs.observe(imgs[i]);
  }

  /**
   * 12-color display palette (zipcushions parity)
   * Used to render color filter chips. `key` matches extracted fabric.colors[].
   */
  var COLOR_PALETTE = [
    { key: 'beige',  label: 'Beige',  swatch: '#E8D9B8' },
    { key: 'grey',   label: 'Grey',   swatch: '#9A9A9A' },
    { key: 'white',  label: 'White',  swatch: '#F5F1EC' },
    { key: 'black',  label: 'Black',  swatch: '#1F1F1F' },
    { key: 'brown',  label: 'Brown',  swatch: '#7A4A20' },
    { key: 'blue',   label: 'Blue',   swatch: '#3B82F6' },
    { key: 'green',  label: 'Green',  swatch: '#16A34A' },
    { key: 'red',    label: 'Red',    swatch: '#DC2626' },
    { key: 'yellow', label: 'Yellow', swatch: '#EAB308' },
    { key: 'orange', label: 'Orange', swatch: '#EA580C' },
    { key: 'pink',   label: 'Pink',   swatch: '#EC4899' },
    { key: 'violet', label: 'Violet', swatch: '#8B5CF6' }
  ];

  /**
   * Hex lookup (shared with detail drawer color dots)
   */
  var COLOR_HEX = {
    beige: '#E8D9B8', grey: '#9A9A9A', white: '#F5F1EC', black: '#1F1F1F',
    brown: '#7A4A20', blue: '#3B82F6', green: '#16A34A', red: '#DC2626',
    yellow: '#EAB308', orange: '#EA580C', pink: '#EC4899', violet: '#8B5CF6'
  };

  /**
   * Collection → category map derived from poufsite/fabric-catalog.json
   * 4 primary categories (1013 fabrics total across 39 collections)
   * Keys are lowercase, prefix-stripped collection names.
   */
  var COLLECTION_CATEGORY_MAP = {
    "armani": "flame-resistant",
    "astral keten": "woven-linen",
    "aura serisi": "woven-linen",
    "bambu keten": "woven-linen",
    "best & petra": "knit",
    "boss": "knit",
    "burn fr - alev almaz": "flame-resistant",
    "cardi & movi": "knit",
    "dego & sarp": "knit",
    "dijitay": "knit",
    "doku & forest": "flame-resistant",
    "dream mira": "knit",
    "duha": "woven-linen",
    "elvis & domino": "woven-linen",
    "gant": "knit",
    "giza": "knit",
    "glass - deri": "flame-resistant",
    "henna & bella & stella": "woven-linen",
    "hugo flora": "knit",
    "jasper & mix": "knit",
    "lapis & malta & frida": "woven-linen",
    "lost & eros": "knit",
    "maldiv & amazon dış mekan outdoor koleksiyonu": "outdoor",
    "matrix & neo": "woven-linen",
    "natural": "woven-linen",
    "nova vega": "woven-linen",
    "nuga": "woven-linen",
    "oslo & ruby": "woven-linen",
    "point velvet": "woven-linen",
    "pool wr % 100 su geçirmez dış mekani kumaşi": "outdoor",
    "puffy & yıldız": "knit",
    "reflex fux": "knit",
    "santa & marin": "outdoor",
    "soffy & yıldız": "knit",
    "solo & dore": "knit",
    "taycan": "knit",
    "teddy bukle & bohem": "knit",
    "todos & santos & palm": "outdoor",
    "vesta & dion": "outdoor"
  };

  /**
   * Collection → feature tags (cross-cutting — water-repellent / pet-friendly)
   */
  var COLLECTION_FEATURE_MAP = {
    "armani": ["pet-friendly", "water-repellent"],
    "boss": ["water-repellent"],
    "doku & forest": ["water-repellent"],
    "duha": ["water-repellent"],
    "gant": ["water-repellent"],
    "lost & eros": ["pet-friendly"],
    "maldiv & amazon dış mekan outdoor koleksiyonu": ["water-repellent"],
    "natural": ["water-repellent"]
  };

  /**
   * Material filter definitions (categories + features, 5 total)
   */
  var MATERIAL_FILTERS = [
    { key: 'flame-resistant', label: 'Flame Resistant', type: 'category' },
    { key: 'woven-linen',     label: 'Woven & Linen',   type: 'category' },
    { key: 'knit',            label: 'Knit',            type: 'category' },
    { key: 'water-repellent', label: 'Water Repellent', type: 'feature' },
    { key: 'pet-friendly',    label: 'Pet Friendly',    type: 'feature' }
  ];

  /* ══ HELPERS ══ */

  function escHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ES5 closest helper (safe with Text nodes)
  function closestEl(el, selector) {
    if (!el || el.nodeType !== 1) {
      el = el && el.parentNode ? el.parentNode : null;
      if (!el || el.nodeType !== 1) return null;
    }
    if (el.closest) return el.closest(selector);
    while (el && el !== document) {
      if ((el.matches || el.msMatchesSelector || el.webkitMatchesSelector).call(el, selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  /**
   * Clean collection name for display — strip Turkish marketing prefixes
   * e.g. "Dış Mekan Outdoor Koleksiyonu | Todos & Santos" → "Todos & Santos"
   */
  function cleanCollectionName(name) {
    if (!name) return '';
    var rawTrim = String(name).replace(/^\s+|\s+$/g, '');
    /* Exact Turkish->short English display names (display only; filter key f.collection untouched) */
    var trMap = {
      'Astral Keten': 'Astral Linen',
      'Bambu Keten': 'Bamboo Linen',
      'Aura Serisi': 'Aura Series',
      'Maldiv & Amazon Dış Mekan Outdoor Koleksiyonu': 'Maldiv & Amazon Outdoor',
      'Pool WR % 100 SU GEÇİRMEZ DIŞ MEKANI KUMAŞI': 'Pool WR Waterproof Outdoor',
      'Burn FR - Alev Almaz': 'Burn FR Flame-Retardant',
      'Soffy & Yıldız': 'Soffy & Star',
      'Puffy & Yıldız': 'Puffy & Star',
      'Teddy Bukle & Bohem': 'Teddy Bouclé & Bohem'
    };
    if (trMap[rawTrim]) return trMap[rawTrim];
    var cleaned = String(name);
    if (cleaned.indexOf('|') > -1) {
      cleaned = cleaned.split('|').pop();
      if (cleaned.replace) cleaned = cleaned.replace(/^\s+|\s+$/g, '');
    }
    var junkPrefixes = [
      /^d[ıi]ş\s*mekan\s*outdoor\s*koleksiyonu[:\s-]*/i,
      /^outdoor\s*koleksiyon[u]?[:\s-]*/i,
      /^i?ç\s*mekan\s*indoor\s*koleksiyonu[:\s-]*/i,
      /^koleksiyon[u]?[:\s-]*/i
    ];
    for (var i = 0; i < junkPrefixes.length; i++) {
      cleaned = cleaned.replace(junkPrefixes[i], '');
    }
    /* strip trailing Turkish outdoor-collection suffix, then translate any stray Turkish words */
    cleaned = cleaned.replace(/[\s|-]*d[ıi]ş\s*mekan\s*outdoor\s*koleksiyonu\s*$/i, '');
    cleaned = cleaned
      .replace(/\bKeten\b/gi, 'Linen')
      .replace(/\bSerisi\b/gi, 'Series')
      .replace(/\bY[ıi]ld[ıi]z\b/gi, 'Star')
      .replace(/\bBukle\b/gi, 'Bouclé')
      .replace(/\bAlev\s*Almaz\b/gi, 'Flame-Retardant');
    cleaned = cleaned.replace(/^\s+|\s+$/g, '');
    return cleaned || name;
  }

  /**
   * Derive a stable color hex from any free-form name (for placeholder bg)
   */
  function getColorFromName(name) {
    if (!name) return '#999';
    var n = String(name).toLowerCase();
    if (n.indexOf('white') > -1 || n.indexOf('cream') > -1) return '#f0ece4';
    if (n.indexOf('black') > -1) return '#2a2a2a';
    if (n.indexOf('navy') > -1) return '#1a3a6b';
    if (n.indexOf('blue') > -1) return '#4a7ab5';
    if (n.indexOf('red') > -1) return '#b54a4a';
    if (n.indexOf('green') > -1 || n.indexOf('forest') > -1) return '#3d7a4a';
    if (n.indexOf('beige') > -1 || n.indexOf('sand') > -1) return '#c4a882';
    if (n.indexOf('grey') > -1 || n.indexOf('gray') > -1) return '#8a8a8a';
    if (n.indexOf('brown') > -1) return '#7a5a3a';
    if (n.indexOf('yellow') > -1) return '#c4a832';
    if (n.indexOf('orange') > -1) return '#c4692a';
    if (n.indexOf('pink') > -1) return '#c46a8a';
    if (n.indexOf('purple') > -1) return '#7a4ac4';
    var hash = 0;
    for (var i = 0; i < n.length; i++) hash = n.charCodeAt(i) + ((hash << 5) - hash);
    return 'hsl(' + (Math.abs(hash) % 360) + ',45%,55%)';
  }

  /**
   * Strip Turkish marketing text and noise from composition string.
   * Ported from cfg-fabric.js cleanComposition() (lines 771-795).
   */
  function cleanComposition(comp) {
    if (!comp) return '';
    var s = String(comp);
    s = s.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
    var fiberMatch = s.match(/(%?\d+\s*%?\s*(?:Poly(?:ester|olefin|propylene|amide)|Cotton|Linen|Acrylic|Viscose|Nylon|Wool|Silk|Pes|PES|PP|PA|Rayon|Lycra|Spandex|Elastane)[a-z]*)/gi);
    if (fiberMatch && fiberMatch.length > 0) {
      var joined = [];
      for (var i = 0; i < fiberMatch.length; i++) {
        var cleaned = fiberMatch[i].replace(/^\s+|\s+$/g, '');
        cleaned = cleaned.replace(/%(\d+)/, '$1%');
        cleaned = cleaned.replace(/^(\d+)\s*(?=%)/, '$1');
        joined.push(cleaned);
      }
      return joined.join(', ');
    }
    s = s.replace(/[^\x00-\x7F]+/g, ' ');
    s = s.replace(/Product\s*Name\s*:\s*\w+/gi, '');
    s = s.replace(/Widht\s*:\s*\d+\s*Cm/gi, '');
    s = s.replace(/Martindale\s*:\s*\d+/gi, '');
    s = s.replace(/,\s*,/g, ',').replace(/\s+/g, ' ').replace(/^[\s,]+|[\s,]+$/g, '');
    if (s.length < 5) return '';
    return s;
  }

  /* ══ FABRIC TAXONOMY FUNCTIONS ══ */

  function getFabricCategory(fabric) {
    if (fabric.category && (
      fabric.category === 'flame-resistant' ||
      fabric.category === 'woven-linen' ||
      fabric.category === 'outdoor' ||
      fabric.category === 'knit'
    )) {
      return fabric.category;
    }
    var col = cleanCollectionName(fabric.collection || '').toLowerCase();
    return COLLECTION_CATEGORY_MAP[col] || fabric.category || null;
  }

  function getFabricFeatures(fabric) {
    if (Array.isArray(fabric.features) && fabric.features.length > 0) {
      return fabric.features;
    }
    var col = cleanCollectionName(fabric.collection || '').toLowerCase();
    return COLLECTION_FEATURE_MAP[col] || [];
  }

  /* ══ FILTER MATCHERS ══ */

  function fabricMatchesColor(fabric, colorKey) {
    if (!colorKey) return true;
    var colors = fabric.colors || [];
    return colors.length > 0 && colors[0] === colorKey;
  }

  function fabricMatchesMaterial(fabric, materialKey) {
    if (!materialKey || materialKey === 'all') return true;
    var category = getFabricCategory(fabric);
    if (category === materialKey) return true;
    var features = getFabricFeatures(fabric);
    for (var i = 0; i < features.length; i++) {
      if (features[i] === materialKey) return true;
    }
    return false;
  }

  function fabricMatchesUsage(fabric, usageKey) {
    if (!usageKey || usageKey === 'all') return true;
    var cat = getFabricCategory(fabric);
    if (usageKey === 'outdoor') return cat === 'outdoor';
    return cat !== 'outdoor';
  }

  function fabricMatchesFeature(fabric, featureKey) {
    if (!featureKey || featureKey === 'all') return true;
    var features = getFabricFeatures(fabric);
    for (var i = 0; i < features.length; i++) {
      if (features[i] === featureKey) return true;
    }
    return false;
  }

  /**
   * Does a fabric pass ALL active filters (excluding the one being varied)?
   * Used for count calculation.
   */
  function passesAllFilters(fabric, state, skipType) {
    if (skipType !== 'color' && !fabricMatchesColor(fabric, state.color)) return false;
    if (skipType !== 'usage' && !fabricMatchesUsage(fabric, state.usage)) return false;
    if (skipType !== 'material' && !fabricMatchesMaterial(fabric, state.material)) return false;
    if (skipType !== 'feature' && !fabricMatchesFeature(fabric, state.feature)) return false;
    return true;
  }

  function countFabricsForColor(allFabrics, state, colorKey) {
    var n = 0;
    for (var i = 0; i < allFabrics.length; i++) {
      var f = allFabrics[i];
      if (!fabricMatchesColor(f, colorKey)) continue;
      if (!fabricMatchesUsage(f, state.usage)) continue;
      if (!fabricMatchesMaterial(f, state.material)) continue;
      if (!fabricMatchesFeature(f, state.feature)) continue;
      n++;
    }
    return n;
  }

  function countFabricsForFilter(allFabrics, state, filterType, filterValue) {
    var n = 0;
    for (var i = 0; i < allFabrics.length; i++) {
      var f = allFabrics[i];
      if (!fabricMatchesColor(f, state.color)) continue;
      var u = filterType === 'usage' ? filterValue : (state.usage || 'all');
      var m = filterType === 'material' ? filterValue : (state.material || 'all');
      var fe = filterType === 'feature' ? filterValue : (state.feature || 'all');
      if (!fabricMatchesUsage(f, u)) continue;
      if (!fabricMatchesMaterial(f, m)) continue;
      if (!fabricMatchesFeature(f, fe)) continue;
      n++;
    }
    return n;
  }

  /* ══ INSTANCE CREATION ══ */

  // Cached across instances (one fetch per page load)
  var fabricCache = null;
  var fabricFetchPromise = null;

  function loadFabrics() {
    if (fabricCache) return Promise.resolve(fabricCache);
    if (fabricFetchPromise) return fabricFetchPromise;

    function normaliseCSV(val) {
      if (Array.isArray(val)) return val;
      if (typeof val !== 'string' || !val) return [];
      var out = val.split(',');
      var cleaned = [];
      for (var i = 0; i < out.length; i++) {
        var v = out[i].replace(/^\s+|\s+$/g, '');
        if (v) cleaned.push(v);
      }
      return cleaned;
    }

    // Parallel fetch: DB fabrics + fabric-metadata.json + fabric-colors.json
    var fabricsP = fetch(APP_PROXY + '/?endpoint=fabrics').then(function(r){
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
    var metadataP = CONFIG.metadataUrl
      ? fetch(CONFIG.metadataUrl).then(function(r){ return r.ok ? r.json() : {}; }).catch(function(){ return {}; })
      : Promise.resolve({});
    var colorsP = CONFIG.colorsUrl
      ? fetch(CONFIG.colorsUrl).then(function(r){ return r.ok ? r.json() : {}; }).catch(function(){ return {}; })
      : Promise.resolve({});

    fabricFetchPromise = Promise.all([fabricsP, metadataP, colorsP])
      .then(function(results) {
        var fabrics = results[0] || [];
        var metadata = results[1] || {};
        var colorMap = results[2] || {};

        for (var i = 0; i < fabrics.length; i++) {
          var f = fabrics[i];
          f.colors = normaliseCSV(f.colors);
          f.features = normaliseCSV(f.features);

          var meta = metadata[f.code] || {};
          var cColors = colorMap[f.code] || [];

          // Color priority: metadata.json (vision-classified) → colors.json → DB
          if (meta.colors && meta.colors.length) {
            f.colors = meta.colors;
          } else if (f.colors.length === 0 && cColors.length > 0) {
            f.colors = cColors;
          }

          // Feature / metadata fallbacks
          if (f.features.length === 0 && meta.features && meta.features.length) {
            f.features = meta.features;
          }
          if (!f.material && meta.material) f.material = meta.material;
          if (!f.category && meta.category) f.category = meta.category;
          if (!f.martindale && meta.martindale) f.martindale = meta.martindale;
          if (!f.weight && meta.weight) f.weight = meta.weight;
          if (!f.width && meta.width) f.width = meta.width;
          if (!f.composition && meta.composition) f.composition = meta.composition;
        }

        fabricCache = fabrics;
        fabricFetchPromise = null;
        return fabrics;
      })
      .catch(function(err) {
        fabricFetchPromise = null;
        throw err;
      });
    return fabricFetchPromise;
  }

  function createInstance(root) {
    var instance = {
      root: root,
      allFabrics: [],
      collections: {},
      state: {
        search: '',
        color: null,
        usage: 'all',
        material: 'all',
        feature: 'all',
        expandedCollections: {},
        activeCollection: null,
        selectedFabric: null,
        favorites: loadFavorites()
      },
      searchTimer: null,
      els: {},
      _initialExpandDone: false,
      _lastFocus: null
    };

    // Cache DOM refs
    instance.els.grid = root.querySelector('[data-pfab-grid]');
    instance.els.search = root.querySelector('[data-pfab-search]');
    instance.els.searchClear = root.querySelector('[data-pfab-search-clear]');
    instance.els.colors = root.querySelector('[data-pfab-colors]');
    instance.els.dropdowns = root.querySelector('[data-pfab-dropdowns]');
    instance.els.activeChips = root.querySelector('[data-pfab-active-chips]');
    instance.els.count = root.querySelector('[data-pfab-count]');
    instance.els.clear = root.querySelector('[data-pfab-clear]');
    instance.els.sheetOpen = root.querySelector('[data-pfab-sheet-open]');
    instance.els.sheet = root.querySelector('[data-pfab-sheet]');
    instance.els.sheetBody = root.querySelector('[data-pfab-sheet-body]');
    instance.els.sheetApply = root.querySelector('[data-pfab-sheet-apply]');
    instance.els.sheetReset = root.querySelector('[data-pfab-sheet-reset]');
    instance.els.sheetCount = root.querySelector('[data-pfab-sheet-count]');
    instance.els.drawer = root.querySelector('[data-pfab-drawer]');
    instance.els.drawerContent = root.querySelector('[data-pfab-drawer-content]');
    instance.els.favsToggle = root.querySelector('[data-pfab-favs-toggle]');
    instance.els.favsCount = root.querySelector('[data-pfab-favs-count]');
    instance.els.favs = root.querySelector('[data-pfab-favs]');
    instance.els.favsContent = root.querySelector('[data-pfab-favs-content]');
    instance.els.favsClear = root.querySelector('[data-pfab-favs-clear]');
    instance.els.activeCount = root.querySelector('[data-pfab-active-count]');

    bindEvents(instance);
    updateFavsBadge(instance);

    // Fetch and render
    loadFabrics()
      .then(function(data) {
        instance.allFabrics = data || [];
        organizeByCollection(instance);
        renderAll(instance);
        applyHashIfPresent(instance);
      })
      .catch(function() {
        if (instance.els.grid) {
          instance.els.grid.innerHTML =
            '<div class="pfab-error">'
            + '<p>Could not load fabrics. Please refresh.</p>'
            + '<button type="button" class="pfab-btn pfab-btn--ghost" onclick="location.reload()">Refresh</button>'
            + '</div>';
        }
      });

    return instance;
  }

  function organizeByCollection(instance) {
    instance.collections = {};
    for (var i = 0; i < instance.allFabrics.length; i++) {
      var f = instance.allFabrics[i];
      if (f.isActive === false) continue;
      var col = f.collection || 'Other';
      if (!instance.collections[col]) {
        instance.collections[col] = {
          name: col,
          category: f.category || 'outdoor',
          fabrics: []
        };
      }
      instance.collections[col].fabrics.push(f);
    }
  }

  /* ══ FILTERING ══ */

  function getFilteredFabrics(instance) {
    var state = instance.state;
    var query = (state.search || '').toLowerCase().replace(/^\s+|\s+$/g, '');
    var result = [];
    for (var i = 0; i < instance.allFabrics.length; i++) {
      var f = instance.allFabrics[i];
      if (f.isActive === false) continue;
      if (!fabricMatchesColor(f, state.color)) continue;
      if (!fabricMatchesUsage(f, state.usage)) continue;
      if (!fabricMatchesMaterial(f, state.material)) continue;
      if (!fabricMatchesFeature(f, state.feature)) continue;

      if (query) {
        var name = (f.name || '').toLowerCase();
        var code = (f.code || '').toLowerCase();
        var col = (f.collection || '').toLowerCase();
        var cleanCol = cleanCollectionName(f.collection || '').toLowerCase();
        var matched = false;
        if (name.indexOf(query) > -1) matched = true;
        else if (code.indexOf(query) > -1) matched = true;
        else if (col.indexOf(query) > -1) matched = true;
        else if (cleanCol.indexOf(query) > -1) matched = true;
        else if (f.colors) {
          for (var ci = 0; ci < f.colors.length; ci++) {
            if (String(f.colors[ci]).toLowerCase().indexOf(query) > -1) {
              matched = true;
              break;
            }
          }
        }
        if (!matched) continue;
      }
      result.push(f);
    }
    return result;
  }

  function groupFabricsByCollection(fabrics) {
    var grouped = {};
    for (var i = 0; i < fabrics.length; i++) {
      var f = fabrics[i];
      var col = f.collection || 'Other';
      if (!grouped[col]) grouped[col] = [];
      grouped[col].push(f);
    }
    return grouped;
  }

  function isFlatMode(state) {
    if (state.search && state.search.replace(/^\s+|\s+$/g, '')) return true;
    if (state.color) return true;
    if (state.usage && state.usage !== 'all') return true;
    if (state.material && state.material !== 'all') return true;
    if (state.feature && state.feature !== 'all') return true;
    return false;
  }

  function activeFilterCount(state) {
    var n = 0;
    if (state.color) n++;
    if (state.usage && state.usage !== 'all') n++;
    if (state.material && state.material !== 'all') n++;
    if (state.feature && state.feature !== 'all') n++;
    return n;
  }

  /* ══ RENDER — GRID ══ */

  function renderAll(instance) {
    renderColorChips(instance);
    renderDropdowns(instance);
    renderActiveChips(instance);
    renderGrid(instance);
    renderSheetBody(instance);
    renderCount(instance);
    updateSearchClear(instance);
    updateClearAllBtn(instance);
    updateMobileBadge(instance);
  }

  function renderGrid(instance) {
    var grid = instance.els.grid;
    if (!grid) return;
    var state = instance.state;

    // Preserve scroll
    var scrollParent = grid.parentNode;
    while (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      var ov = window.getComputedStyle(scrollParent).overflowY;
      if (ov === 'auto' || ov === 'scroll') break;
      scrollParent = scrollParent.parentNode;
    }
    var savedWindowY = window.pageYOffset;
    var savedParentY = (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement)
      ? scrollParent.scrollTop : 0;

    var html = '';
    var flat = isFlatMode(state);

    if (flat) {
      // Any filter / search active → flat mode
      var filtered = getFilteredFabrics(instance);
      if (filtered.length === 0) {
        html = '<div class="pfab-empty">'
          + '<p>No fabrics match your filters.</p>'
          + '<button type="button" class="pfab-btn pfab-btn--ghost" data-pfab-clear-inline>Clear filters</button>'
          + '</div>';
      } else {
        html = renderFlatGrid(instance, filtered);
      }
    } else if (state.activeCollection) {
      // Viewing a single collection
      var fabricsInCol = [];
      for (var i = 0; i < instance.allFabrics.length; i++) {
        if (instance.allFabrics[i].collection === state.activeCollection) {
          fabricsInCol.push(instance.allFabrics[i]);
        }
      }
      html = renderCollectionDetail(instance, state.activeCollection, fabricsInCol);
    } else {
      // Default: collection cards grid
      html = renderCollectionCards(instance);
    }
    grid.innerHTML = html;
    kickLazyImages(grid);

    if (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      scrollParent.scrollTop = savedParentY;
    }
    window.scrollTo(0, savedWindowY);
  }

  function renderFlatGrid(instance, fabrics) {
    var html = '<div class="pfab-flat"><div class="pfab-grid">';
    for (var i = 0; i < fabrics.length; i++) {
      html += renderCard(instance, fabrics[i]);
    }
    html += '</div></div>';
    return html;
  }

  /**
   * Render the default browse view: a grid of collection cards, each with
   * a 2×2 mosaic preview of sample fabrics. Click opens the collection.
   */
  function renderCollectionCards(instance) {
    var grouped = groupFabricsByCollection(instance.allFabrics);
    var keys = [];
    for (var k in grouped) if (grouped.hasOwnProperty(k)) keys.push(k);
    keys.sort();

    var html = '<div class="pfab-colcards">';
    for (var i = 0; i < keys.length; i++) {
      var colName = keys[i];
      var colFabrics = grouped[colName];
      var clean = cleanCollectionName(colName);

      // Pick 4 samples spaced across the collection for better variety
      var samples = [];
      if (colFabrics.length > 0) {
        var step = colFabrics.length >= 4 ? Math.floor(colFabrics.length / 4) : 1;
        for (var j = 0; j < colFabrics.length && samples.length < 4; j += Math.max(step, 1)) {
          if (colFabrics[j].imageUrl) samples.push(colFabrics[j]);
        }
        // Fallback: fill with any images in order
        if (samples.length < 4) {
          for (var f = 0; f < colFabrics.length && samples.length < 4; f++) {
            if (colFabrics[f].imageUrl) {
              var already = false;
              for (var s = 0; s < samples.length; s++) {
                if (samples[s].code === colFabrics[f].code) { already = true; break; }
              }
              if (!already) samples.push(colFabrics[f]);
            }
          }
        }
      }
      while (samples.length < 4) samples.push({ imageUrl: null, code: '' });

      // Determine collection meta category for eyebrow (Flame Resistant / Outdoor / etc)
      var catKey = colFabrics[0] && getFabricCategory(colFabrics[0]);
      var catLabel = '';
      if (catKey === 'flame-resistant') catLabel = 'Flame Resistant';
      else if (catKey === 'woven-linen') catLabel = 'Woven & Linen';
      else if (catKey === 'knit') catLabel = 'Knit';
      else if (catKey === 'outdoor') catLabel = 'Outdoor';

      html += '<article class="pfab-colcard" data-pfab-opencol="' + escHtml(colName) + '" tabindex="0" role="button" aria-label="Open ' + escHtml(clean) + ' collection">';
      html += '<div class="pfab-colcard__mosaic">';
      for (var m = 0; m < 4; m++) {
        var sm = samples[m];
        if (sm.imageUrl) {
          // Above-the-fold first 8 colcards (i < 8) → eager-load for fast first paint
          var tileLoading = i < 8 ? 'eager' : 'lazy';
          // Mosaic tile display ~150-170px. Serve 300w (2x retina).
          var tileSrc = toImg(sm.imageUrl, 300);
          var tileSrcset = srcset(sm.imageUrl, [200, 300, 400]);
          html += '<div class="pfab-colcard__tile">'
            + '<img src="' + escHtml(tileSrc) + '"'
            + (tileSrcset ? ' srcset="' + escHtml(tileSrcset) + '" sizes="(min-width:1024px) 160px, (min-width:640px) 130px, 90px"' : '')
            + ' alt="" loading="' + tileLoading + '" decoding="async"'
            + ' onload="this.classList.add(\'is-loaded\')"'
            + ' onerror="this.parentNode.classList.add(\'pfab-colcard__tile--ph\');this.remove()" />'
            + '</div>';
        } else {
          html += '<div class="pfab-colcard__tile pfab-colcard__tile--ph"></div>';
        }
      }
      html += '</div>';
      html += '<div class="pfab-colcard__body">';
      if (catLabel) {
        html += '<p class="pfab-colcard__eyebrow">' + escHtml(catLabel) + '</p>';
      }
      html += '<h3 class="pfab-colcard__name">' + escHtml(clean) + '</h3>';
      html += '<div class="pfab-colcard__foot">';
      html += '<span class="pfab-colcard__count">' + colFabrics.length + ' fabrics</span>';
      html += '<span class="pfab-colcard__cta">Explore <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></span>';
      html += '</div>';
      html += '</div>';
      html += '</article>';
    }
    html += '</div>';
    return html;
  }

  /**
   * Render a single collection's fabrics — triggered when user clicks a
   * collection card. Includes a "Back to all collections" header.
   */
  function renderCollectionDetail(instance, colName, fabrics) {
    var clean = cleanCollectionName(colName);
    var html = '<div class="pfab-coldetail">';
    html += '<header class="pfab-coldetail__head">';
    html += '<button type="button" class="pfab-coldetail__back" data-pfab-back-to-cols>';
    html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>';
    html += '<span>All collections</span>';
    html += '</button>';
    html += '<div class="pfab-coldetail__info">';
    html += '<h2 class="pfab-coldetail__title">' + escHtml(clean) + '</h2>';
    html += '<p class="pfab-coldetail__count">' + fabrics.length + ' fabrics</p>';
    html += '</div>';
    html += '</header>';
    html += '<div class="pfab-grid">';
    for (var i = 0; i < fabrics.length; i++) {
      html += renderCard(instance, fabrics[i]);
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  function renderCard(instance, fabric) {
    var isFav = isFavorite(instance, fabric.id);
    var surcharge = (fabric.priceSurcharge && fabric.priceSurcharge > 0)
      ? '<span class="pfab-card__surcharge">+$' + (Math.round(fabric.priceSurcharge * 100) / 100).toFixed(2) + '</span>'
      : '';
    var imgHtml;
    var placeholderBg = getColorFromName(fabric.colorFamily || fabric.name);
    var placeholderLabel = escHtml(fabric.code || fabric.name || '');
    if (fabric.imageUrl) {
      // Card display ~180-220px. Serve 440w default + srcset for retina/large screens.
      var cardSrc = toImg(fabric.imageUrl, 440);
      var cardSrcset = srcset(fabric.imageUrl, [200, 300, 440, 600]);
      imgHtml = '<img class="pfab-card__img" src="' + escHtml(cardSrc) + '"'
        + (cardSrcset ? ' srcset="' + escHtml(cardSrcset) + '" sizes="(min-width:1280px) 200px, (min-width:768px) 180px, 48vw"' : '')
        + ' alt="' + escHtml(fabric.name || fabric.code || 'Fabric') + '"'
        + ' loading="lazy" decoding="async"'
        + ' onload="this.classList.add(\'is-loaded\')"'
        + ' onerror="var p=this.parentNode;this.remove();var d=document.createElement(\'div\');d.className=\'pfab-card__img pfab-card__img--placeholder\';d.style.background=\'' + placeholderBg + '\';d.textContent=\'' + placeholderLabel.replace(/'/g, "\\'") + '\';p.appendChild(d);" />';
    } else {
      imgHtml = '<div class="pfab-card__img pfab-card__img--placeholder" style="background:'
        + placeholderBg
        + ';">' + placeholderLabel + '</div>';
    }

    var heartPath = 'M12 21s-7.5-4.5-9.5-9.5C1 7.5 3.5 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3.5 0 6 3.5 4.5 7.5C19.5 16.5 12 21 12 21z';
    var cardHtml = '<article class="pfab-card" data-pfab-card data-fabric-id="' + escHtml(fabric.id) + '">';
    cardHtml += '<div class="pfab-card__imgwrap">';
    cardHtml += imgHtml;
    cardHtml += '<button type="button" class="pfab-card__heart' + (isFav ? ' is-active' : '')
      + '" data-pfab-heart data-fabric-id="' + escHtml(fabric.id) + '"'
      + ' aria-label="' + (isFav ? 'Remove from favorites' : 'Add to favorites') + '"'
      + ' aria-pressed="' + (isFav ? 'true' : 'false') + '">';
    cardHtml += '<svg width="20" height="20" viewBox="0 0 24 24" fill="' + (isFav ? 'currentColor' : 'none')
      + '" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<path d="' + heartPath + '"/></svg>';
    cardHtml += '</button>';
    cardHtml += '</div>';
    cardHtml += '<div class="pfab-card__meta">';
    cardHtml += '<p class="pfab-card__collection">' + escHtml(cleanCollectionName(fabric.collection || '')) + '</p>';
    cardHtml += '<h3 class="pfab-card__name">' + escHtml(fabric.name || fabric.code || '') + '</h3>';
    cardHtml += surcharge;
    cardHtml += '</div>';
    cardHtml += '</article>';
    return cardHtml;
  }

  /* ══ RENDER — COLOR CHIPS ══ */

  function renderColorChips(instance) {
    var host = instance.els.colors;
    if (!host) return;
    var html = '';
    for (var i = 0; i < COLOR_PALETTE.length; i++) {
      var c = COLOR_PALETTE[i];
      var count = countFabricsForColor(instance.allFabrics, instance.state, c.key);
      var isActive = instance.state.color === c.key;
      var disabled = count === 0 && !isActive;
      var cls = 'pfab-chip pfab-chip--color';
      if (isActive) cls += ' pfab-chip--active';
      if (disabled) cls += ' pfab-chip--disabled';
      html += '<button type="button" class="' + cls + '"'
        + ' data-pfab-color="' + escHtml(c.key) + '"'
        + ' style="--swatch:' + c.swatch + '"'
        + (disabled ? ' disabled aria-disabled="true"' : '')
        + ' aria-pressed="' + (isActive ? 'true' : 'false') + '">'
        + '<span class="pfab-chip__swatch" aria-hidden="true"></span>'
        + '<span class="pfab-chip__label">' + escHtml(c.label) + '</span>'
        + '<span class="pfab-chip__count">' + count + '</span>'
        + '</button>';
    }
    host.innerHTML = html;
  }

  /* ══ RENDER — DROPDOWNS ══ */

  function renderDropdowns(instance) {
    var host = instance.els.dropdowns;
    if (!host) return;
    var state = instance.state;
    var html = '';

    // Usage
    var usageOptions = [
      { key: 'all', label: 'All' },
      { key: 'outdoor', label: 'Outdoor' },
      { key: 'indoor', label: 'Indoor' }
    ];
    var usageLabel = state.usage === 'all' ? 'Usage'
      : (state.usage === 'outdoor' ? 'Outdoor' : 'Indoor');
    html += buildDropdown(instance, 'usage', 'Usage', usageLabel, usageOptions, state.usage || 'all');

    // Material
    var matOptions = [{ key: 'all', label: 'All' }];
    for (var m = 0; m < MATERIAL_FILTERS.length; m++) {
      if (MATERIAL_FILTERS[m].type === 'category') {
        matOptions.push({ key: MATERIAL_FILTERS[m].key, label: MATERIAL_FILTERS[m].label });
      }
    }
    var matLabel = state.material === 'all' ? 'Material' : findLabel(MATERIAL_FILTERS, state.material, 'Material');
    html += buildDropdown(instance, 'material', 'Material', matLabel, matOptions, state.material || 'all');

    // Features
    var featOptions = [{ key: 'all', label: 'All' }];
    for (var mf = 0; mf < MATERIAL_FILTERS.length; mf++) {
      if (MATERIAL_FILTERS[mf].type === 'feature') {
        featOptions.push({ key: MATERIAL_FILTERS[mf].key, label: MATERIAL_FILTERS[mf].label });
      }
    }
    var featLabel = state.feature === 'all' ? 'Features' : findLabel(MATERIAL_FILTERS, state.feature, 'Features');
    html += buildDropdown(instance, 'feature', 'Features', featLabel, featOptions, state.feature || 'all');

    host.innerHTML = html;
  }

  function findLabel(list, key, fallback) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === key) return list[i].label;
    }
    return fallback;
  }

  function buildDropdown(instance, filterType, baseLabel, currentLabel, options, activeKey) {
    var isActive = activeKey && activeKey !== 'all';
    var cls = 'pfab-dd' + (isActive ? ' pfab-dd--active' : '');
    var h = '<div class="' + cls + '" data-pfab-dd="' + filterType + '">';
    h += '<button type="button" class="pfab-dd__btn" data-pfab-dd-btn="' + filterType + '" aria-haspopup="listbox" aria-expanded="false">';
    h += '<span class="pfab-dd__label">' + escHtml(currentLabel) + '</span>';
    h += '<svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    h += '</button>';
    h += '<div class="pfab-dd__menu" data-pfab-dd-menu="' + filterType + '" role="listbox">';
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var count = countFabricsForFilter(instance.allFabrics, instance.state, filterType, opt.key);
      var sel = opt.key === activeKey;
      var disabled = count === 0 && !sel && opt.key !== 'all';
      var itemCls = 'pfab-dd__item'
        + (sel ? ' pfab-dd__item--active' : '')
        + (disabled ? ' pfab-dd__item--disabled' : '');
      h += '<button type="button" class="' + itemCls + '"'
        + (disabled ? ' disabled aria-disabled="true"' : '')
        + ' role="option" aria-selected="' + (sel ? 'true' : 'false') + '"'
        + ' data-pfab-filter-type="' + filterType + '" data-pfab-filter-value="' + escHtml(opt.key) + '">'
        + '<span>' + escHtml(opt.label) + '</span>'
        + '<span class="pfab-dd__item-count">' + count + '</span>'
        + '</button>';
    }
    h += '</div></div>';
    return h;
  }

  /* ══ RENDER — ACTIVE CHIPS ══ */

  function renderActiveChips(instance) {
    var host = instance.els.activeChips;
    if (!host) return;
    var state = instance.state;
    var html = '';

    if (state.color) {
      var colorLabel = findLabel(COLOR_PALETTE, state.color, state.color);
      html += chip('color', colorLabel);
    }
    if (state.usage && state.usage !== 'all') {
      html += chip('usage', state.usage === 'outdoor' ? 'Outdoor' : 'Indoor');
    }
    if (state.material && state.material !== 'all') {
      html += chip('material', findLabel(MATERIAL_FILTERS, state.material, state.material));
    }
    if (state.feature && state.feature !== 'all') {
      html += chip('feature', findLabel(MATERIAL_FILTERS, state.feature, state.feature));
    }
    host.innerHTML = html;

    function chip(type, label) {
      return '<span class="pfab-active-chip" data-pfab-active-chip="' + type + '">'
        + escHtml(label)
        + '<button type="button" aria-label="Remove ' + escHtml(label) + ' filter" data-pfab-remove="' + type + '">'
        + '<svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">'
        + '<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
        + '</svg>'
        + '</button>'
        + '</span>';
    }
  }

  /* ══ RENDER — SHEET (mobile filters) ══ */

  function renderSheetBody(instance) {
    var host = instance.els.sheetBody;
    if (!host) return;
    var state = instance.state;
    var html = '';

    // Color grid
    html += '<section class="pfab-sheet__group">';
    html += '<h4 class="pfab-sheet__grouplabel">Color</h4>';
    html += '<div class="pfab-sheet__colors">';
    for (var i = 0; i < COLOR_PALETTE.length; i++) {
      var c = COLOR_PALETTE[i];
      var count = countFabricsForColor(instance.allFabrics, state, c.key);
      var isActive = state.color === c.key;
      var disabled = count === 0 && !isActive;
      var cls = 'pfab-chip pfab-chip--color';
      if (isActive) cls += ' pfab-chip--active';
      if (disabled) cls += ' pfab-chip--disabled';
      html += '<button type="button" class="' + cls + '"'
        + ' data-pfab-color="' + escHtml(c.key) + '"'
        + ' style="--swatch:' + c.swatch + '"'
        + (disabled ? ' disabled aria-disabled="true"' : '') + '>'
        + '<span class="pfab-chip__swatch" aria-hidden="true"></span>'
        + '<span class="pfab-chip__label">' + escHtml(c.label) + '</span>'
        + '<span class="pfab-chip__count">' + count + '</span>'
        + '</button>';
    }
    html += '</div></section>';

    // Usage
    html += buildSheetGroup(instance, 'Usage', 'usage', [
      { key: 'all', label: 'All' },
      { key: 'outdoor', label: 'Outdoor' },
      { key: 'indoor', label: 'Indoor' }
    ]);

    // Material
    var matOpts = [{ key: 'all', label: 'All' }];
    for (var mm = 0; mm < MATERIAL_FILTERS.length; mm++) {
      if (MATERIAL_FILTERS[mm].type === 'category') {
        matOpts.push({ key: MATERIAL_FILTERS[mm].key, label: MATERIAL_FILTERS[mm].label });
      }
    }
    html += buildSheetGroup(instance, 'Material', 'material', matOpts);

    // Features
    var featOpts = [{ key: 'all', label: 'All' }];
    for (var mf = 0; mf < MATERIAL_FILTERS.length; mf++) {
      if (MATERIAL_FILTERS[mf].type === 'feature') {
        featOpts.push({ key: MATERIAL_FILTERS[mf].key, label: MATERIAL_FILTERS[mf].label });
      }
    }
    html += buildSheetGroup(instance, 'Features', 'feature', featOpts);

    host.innerHTML = html;

    // Update apply count
    var filtered = getFilteredFabrics(instance);
    if (instance.els.sheetCount) {
      instance.els.sheetCount.textContent = String(filtered.length);
    }
  }

  function buildSheetGroup(instance, label, filterType, options) {
    var state = instance.state;
    var activeKey = state[filterType] || 'all';
    var h = '<section class="pfab-sheet__group">';
    h += '<h4 class="pfab-sheet__grouplabel">' + escHtml(label) + '</h4>';
    h += '<div class="pfab-sheet__options">';
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var count = countFabricsForFilter(instance.allFabrics, state, filterType, opt.key);
      var sel = opt.key === activeKey;
      var disabled = count === 0 && !sel && opt.key !== 'all';
      var cls = 'pfab-sheet__option' + (sel ? ' is-active' : '') + (disabled ? ' is-disabled' : '');
      h += '<button type="button" class="' + cls + '"'
        + (disabled ? ' disabled aria-disabled="true"' : '')
        + ' data-pfab-filter-type="' + filterType + '"'
        + ' data-pfab-filter-value="' + escHtml(opt.key) + '">'
        + '<span>' + escHtml(opt.label) + '</span>'
        + '<span class="pfab-sheet__option-count">' + count + '</span>'
        + '</button>';
    }
    h += '</div></section>';
    return h;
  }

  /* ══ RENDER — COUNT & BADGES ══ */

  function renderCount(instance) {
    if (!instance.els.count) return;
    var filtered = getFilteredFabrics(instance);
    var parts = [filtered.length + ' fabric' + (filtered.length === 1 ? '' : 's')];
    var state = instance.state;
    if (state.color) {
      parts.push(findLabel(COLOR_PALETTE, state.color, state.color));
    }
    if (state.usage && state.usage !== 'all') {
      parts.push(state.usage === 'outdoor' ? 'Outdoor' : 'Indoor');
    }
    if (state.material && state.material !== 'all') {
      parts.push(findLabel(MATERIAL_FILTERS, state.material, ''));
    }
    if (state.feature && state.feature !== 'all') {
      parts.push(findLabel(MATERIAL_FILTERS, state.feature, ''));
    }
    instance.els.count.textContent = parts.join(' · ');
  }

  function updateSearchClear(instance) {
    if (!instance.els.searchClear) return;
    if (instance.state.search) {
      instance.els.searchClear.removeAttribute('hidden');
    } else {
      instance.els.searchClear.setAttribute('hidden', '');
    }
  }

  function updateClearAllBtn(instance) {
    if (!instance.els.clear) return;
    var hasAny = !!instance.state.search
      || activeFilterCount(instance.state) > 0;
    if (hasAny) {
      instance.els.clear.removeAttribute('hidden');
    } else {
      instance.els.clear.setAttribute('hidden', '');
    }
  }

  function updateMobileBadge(instance) {
    if (!instance.els.activeCount) return;
    var n = activeFilterCount(instance.state);
    if (n > 0) {
      instance.els.activeCount.textContent = String(n);
      instance.els.activeCount.removeAttribute('hidden');
    } else {
      instance.els.activeCount.textContent = '0';
      instance.els.activeCount.setAttribute('hidden', '');
    }
  }

  /* ══ FAVORITES ══ */

  function loadFavorites() {
    try {
      var raw = localStorage.getItem(FAV_STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveFavorites(favs) {
    try {
      localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(favs));
    } catch (e) {
      // localStorage unavailable — silently ignore
    }
  }

  function isFavorite(instance, id) {
    var favs = instance.state.favorites || [];
    for (var i = 0; i < favs.length; i++) {
      if (String(favs[i]) === String(id)) return true;
    }
    return false;
  }

  function toggleFavorite(instance, id) {
    var favs = instance.state.favorites || [];
    var nextFavs = [];
    var removed = false;
    for (var i = 0; i < favs.length; i++) {
      if (String(favs[i]) === String(id)) {
        removed = true;
      } else {
        nextFavs.push(favs[i]);
      }
    }
    if (!removed) nextFavs.push(id);
    instance.state.favorites = nextFavs;
    saveFavorites(nextFavs);
    syncFavoriteButtons(instance, id, !removed);
    updateFavsBadge(instance);
    renderFavoritesDrawer(instance);
  }

  function syncFavoriteButtons(instance, id, isOn) {
    // Update all heart buttons for this fabric ID (may appear in grid + detail)
    var btns = instance.root.querySelectorAll('[data-pfab-heart][data-fabric-id="' + id + '"], [data-pfab-fav-toggle][data-fabric-id="' + id + '"]');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (isOn) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
      // Inline SVG fill toggle (cards)
      var svg = btn.querySelector('svg');
      if (svg) svg.setAttribute('fill', isOn ? 'currentColor' : 'none');
      // Update "Save to favorites" label inside detail drawer
      if (btn.getAttribute('data-pfab-fav-toggle') !== null) {
        btn.textContent = isOn ? 'Saved to favorites' : 'Save to favorites';
      }
    }
  }

  function updateFavsBadge(instance) {
    if (!instance.els.favsCount) return;
    var n = (instance.state.favorites || []).length;
    instance.els.favsCount.textContent = String(n);
  }

  function renderFavoritesDrawer(instance) {
    var host = instance.els.favsContent;
    if (!host) return;
    var favs = instance.state.favorites || [];
    if (favs.length === 0) {
      host.innerHTML = '<p class="pfab-favs-empty">Tap the \u2661 on any fabric to save it here.</p>';
      return;
    }
    // Resolve each ID → fabric
    var items = [];
    for (var i = 0; i < favs.length; i++) {
      var id = favs[i];
      var fabric = findFabricById(instance, id);
      if (fabric) items.push(fabric);
    }
    if (items.length === 0) {
      host.innerHTML = '<p class="pfab-favs-empty">Tap the \u2661 on any fabric to save it here.</p>';
      return;
    }
    var html = '<ul class="pfab-favs-list">';
    for (var j = 0; j < items.length; j++) {
      var f = items[j];
      var imgHtml;
      if (f.imageUrl) {
        imgHtml = '<img src="' + escHtml(f.imageUrl) + '" alt="' + escHtml(f.name || '') + '" loading="lazy" />';
      } else {
        imgHtml = '<div class="pfab-favs-item__placeholder" style="background:' + getColorFromName(f.colorFamily || f.name) + '"></div>';
      }
      html += '<li class="pfab-favs-item" data-pfab-favs-item data-fabric-id="' + escHtml(f.id) + '">';
      html += imgHtml;
      html += '<div class="pfab-favs-item__body">';
      html += '<p class="pfab-favs-item__name">' + escHtml(f.name || '') + '</p>';
      html += '<p class="pfab-favs-item__col">' + escHtml(cleanCollectionName(f.collection || '')) + '</p>';
      html += '</div>';
      html += '<button type="button" class="pfab-favs-item__remove" data-fav-remove data-fabric-id="' + escHtml(f.id) + '" aria-label="Remove from favorites">';
      html += '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      html += '</button>';
      html += '</li>';
    }
    html += '</ul>';
    host.innerHTML = html;
    kickLazyImages(host);
  }

  /* ══ DETAIL DRAWER ══ */

  function findFabricById(instance, id) {
    for (var i = 0; i < instance.allFabrics.length; i++) {
      if (String(instance.allFabrics[i].id) === String(id)) return instance.allFabrics[i];
    }
    return null;
  }

  function findFabricByCode(instance, code) {
    var needle = String(code).toLowerCase();
    for (var i = 0; i < instance.allFabrics.length; i++) {
      if (instance.allFabrics[i].code && String(instance.allFabrics[i].code).toLowerCase() === needle) {
        return instance.allFabrics[i];
      }
    }
    return null;
  }

  function openDetailDrawer(instance, fabric) {
    if (!fabric || !instance.els.drawer || !instance.els.drawerContent) return;
    instance.state.selectedFabric = fabric;
    instance._lastFocus = document.activeElement;

    var html = '<div class="pfab-detail">';
    html += '<div class="pfab-detail__image">';
    if (fabric.imageUrl) {
      var phBg = getColorFromName(fabric.colorFamily || fabric.name);
      var phLbl = escHtml(fabric.code || fabric.name || '').replace(/'/g, "\\'");
      // Detail drawer displays image at ~430x260 (max 480 wide panel). Serve 800w for retina.
      var detSrc = toImg(fabric.imageUrl, 800);
      var detSrcset = srcset(fabric.imageUrl, [480, 700, 900, 1200]);
      html += '<img src="' + escHtml(detSrc) + '"'
        + (detSrcset ? ' srcset="' + escHtml(detSrcset) + '" sizes="(min-width:1024px) 480px, 100vw"' : '')
        + ' alt="' + escHtml(fabric.name || '') + '"'
        + ' onload="this.classList.add(\'is-loaded\')"'
        + ' onerror="var p=this.parentNode;this.remove();var d=document.createElement(\'div\');d.className=\'pfab-detail__placeholder\';d.style.background=\'' + phBg + '\';d.textContent=\'' + phLbl + '\';p.appendChild(d);" />';
    } else {
      html += '<div class="pfab-detail__placeholder" style="background:' + getColorFromName(fabric.colorFamily || fabric.name) + ';">'
        + escHtml(fabric.code || fabric.name || '') + '</div>';
    }
    html += '</div>';
    html += '<div class="pfab-detail__body">';
    html += '<p class="pfab-detail__eyebrow">' + escHtml(cleanCollectionName(fabric.collection || '')) + '</p>';
    html += '<h2 class="pfab-detail__title">' + escHtml(fabric.name || '') + '</h2>';
    if (fabric.code) html += '<p class="pfab-detail__code">' + escHtml(fabric.code) + '</p>';

    // Specs
    html += '<ul class="pfab-detail__specs">';
    var specs = buildDetailSpecs(fabric);
    for (var s = 0; s < specs.length; s++) {
      html += '<li><span>' + escHtml(specs[s][0]) + '</span><span>' + specs[s][1] + '</span></li>';
    }
    html += '</ul>';

    // Colors
    if (fabric.colors && fabric.colors.length > 0) {
      html += '<div class="pfab-detail__colors">';
      for (var c = 0; c < fabric.colors.length; c++) {
        var ck = fabric.colors[c];
        var hex = COLOR_HEX[ck] || getColorFromName(ck);
        var label = String(ck).charAt(0).toUpperCase() + String(ck).slice(1);
        html += '<span class="pfab-detail__color">'
          + '<span class="pfab-detail__color-dot" style="background:' + hex + ';"></span>'
          + escHtml(label) + '</span>';
      }
      html += '</div>';
    }

    // Features
    var feats = getFabricFeatures(fabric);
    if (feats.length > 0) {
      html += '<div class="pfab-detail__features">';
      for (var ff = 0; ff < feats.length; ff++) {
        var fkey = feats[ff];
        var flabel = fkey.replace(/-/g, ' ').replace(/\b\w/g, function(x) { return x.toUpperCase(); });
        html += '<span class="pfab-detail__feature">' + escHtml(flabel) + '</span>';
      }
      html += '</div>';
    }

    // Surcharge
    if (fabric.priceSurcharge && fabric.priceSurcharge > 0) {
      html += '<div class="pfab-detail__surcharge">Premium fabric <b>+$'
        + (Math.round(fabric.priceSurcharge * 100) / 100).toFixed(2) + '</b></div>';
    }

    // Actions
    var isFav = isFavorite(instance, fabric.id);
    html += '<div class="pfab-detail__actions">';
    html += '<button type="button" class="pfab-btn pfab-btn--ghost'
      + (isFav ? ' is-active' : '')
      + '" data-pfab-fav-toggle data-fabric-id="' + escHtml(fabric.id) + '"'
      + ' aria-pressed="' + (isFav ? 'true' : 'false') + '">'
      + (isFav ? 'Saved to favorites' : 'Save to favorites') + '</button>';
    var builderHref = BUILDER_URL
      + (BUILDER_URL.indexOf('?') > -1 ? '&' : '?')
      + 'fabric=' + encodeURIComponent(fabric.code || '');
    html += '<a class="pfab-btn pfab-btn--primary" href="' + escHtml(builderHref) + '">Design Your Cushion &rarr;</a>';
    html += '</div>';

    html += '</div></div>';
    instance.els.drawerContent.innerHTML = html;
    kickLazyImages(instance.els.drawerContent);
    instance.els.drawer.classList.add('is-open');
    instance.els.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pfab-no-scroll');

    // Deep link
    if (fabric.code) {
      try {
        var newHash = '#fabric=' + encodeURIComponent(fabric.code);
        if (location.hash !== newHash) {
          history.replaceState(null, '', location.pathname + location.search + newHash);
        }
      } catch (e) { /* ignore */ }
    }

    // Focus trap — focus first focusable
    focusFirst(instance.els.drawer);
  }

  function buildDetailSpecs(fabric) {
    var specs = [];
    // Material: default to Polyester if missing (domain default)
    var material = fabric.material
      ? String(fabric.material).charAt(0).toUpperCase() + String(fabric.material).slice(1)
      : 'Polyester';
    specs.push(['Material', escHtml(material)]);

    var comp = cleanComposition(fabric.composition);
    if (comp && comp.length > 3) {
      specs.push(['Composition', escHtml(comp)]);
    } else {
      specs.push(['Composition', '100% Polyester']);
    }

    // Category
    var cat = getFabricCategory(fabric);
    var catLabel = cat
      ? cat.replace(/-/g, ' ').replace(/\b\w/g, function(x) { return x.toUpperCase(); })
      : 'Flame Resistant';
    specs.push(['Category', escHtml(catLabel)]);

    // Martindale
    var martindaleStr = fabric.martindale
      ? String(fabric.martindale).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' cycles'
      : '40,000 cycles';
    specs.push(['Martindale', escHtml(martindaleStr)]);

    var widthStr = fabric.width ? (fabric.width + ' cm') : '140 cm';
    specs.push(['Width', escHtml(widthStr)]);

    var weightStr = fabric.weight ? (fabric.weight + ' g/m\u00b2') : '330 g/m\u00b2';
    specs.push(['Weight', escHtml(weightStr)]);

    return specs;
  }

  function closeDetailDrawer(instance) {
    if (!instance.els.drawer) return;
    instance.els.drawer.classList.remove('is-open');
    instance.els.drawer.setAttribute('aria-hidden', 'true');
    instance.state.selectedFabric = null;
    if (!isAnyDrawerOpen(instance)) {
      document.body.classList.remove('pfab-no-scroll');
    }
    // Clear hash
    try {
      if (location.hash.indexOf('#fabric=') === 0) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    } catch (e) { /* ignore */ }
    // Restore focus
    if (instance._lastFocus && instance._lastFocus.focus) {
      try { instance._lastFocus.focus(); } catch (e) { /* ignore */ }
    }
  }

  /* ══ FAVORITES DRAWER ══ */

  function openFavsDrawer(instance) {
    if (!instance.els.favs) return;
    renderFavoritesDrawer(instance);
    instance.els.favs.classList.add('is-open');
    instance.els.favs.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pfab-no-scroll');
    focusFirst(instance.els.favs);
  }

  function closeFavsDrawer(instance) {
    if (!instance.els.favs) return;
    instance.els.favs.classList.remove('is-open');
    instance.els.favs.setAttribute('aria-hidden', 'true');
    if (!isAnyDrawerOpen(instance)) {
      document.body.classList.remove('pfab-no-scroll');
    }
  }

  /* ══ SHEET (mobile filters) ══ */

  function openSheet(instance) {
    if (!instance.els.sheet) return;
    renderSheetBody(instance);
    instance.els.sheet.classList.add('is-open');
    instance.els.sheet.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pfab-no-scroll');
    focusFirst(instance.els.sheet);
  }

  function closeSheet(instance) {
    if (!instance.els.sheet) return;
    instance.els.sheet.classList.remove('is-open');
    instance.els.sheet.setAttribute('aria-hidden', 'true');
    if (!isAnyDrawerOpen(instance)) {
      document.body.classList.remove('pfab-no-scroll');
    }
  }

  function isAnyDrawerOpen(instance) {
    var a = instance.els.drawer && instance.els.drawer.classList.contains('is-open');
    var b = instance.els.favs && instance.els.favs.classList.contains('is-open');
    var c = instance.els.sheet && instance.els.sheet.classList.contains('is-open');
    return !!(a || b || c);
  }

  function focusFirst(root) {
    if (!root) return;
    var focusable = root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (focusable.length > 0) {
      try { focusable[0].focus(); } catch (e) { /* ignore */ }
    }
  }

  function trapFocus(root, event) {
    if (!root || event.key !== 'Tab') return;
    var focusable = root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      try { last.focus(); } catch (e) { /* ignore */ }
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      try { first.focus(); } catch (e) { /* ignore */ }
    }
  }

  /* ══ STATE TRANSITIONS ══ */

  function setColor(instance, colorKey) {
    if (instance.state.color === colorKey) {
      instance.state.color = null;
    } else {
      instance.state.color = colorKey;
    }
    renderAll(instance);
  }

  function setFilter(instance, filterType, value) {
    if (filterType === 'usage' || filterType === 'material' || filterType === 'feature') {
      instance.state[filterType] = value;
      renderAll(instance);
    }
  }

  function removeFilter(instance, filterType) {
    if (filterType === 'color') {
      instance.state.color = null;
    } else if (filterType === 'usage' || filterType === 'material' || filterType === 'feature') {
      instance.state[filterType] = 'all';
    }
    renderAll(instance);
  }

  function clearAllFilters(instance) {
    instance.state.color = null;
    instance.state.usage = 'all';
    instance.state.material = 'all';
    instance.state.feature = 'all';
    instance.state.search = '';
    if (instance.els.search) instance.els.search.value = '';
    renderAll(instance);
  }

  function setSearch(instance, value) {
    instance.state.search = String(value || '');
    renderAll(instance);
  }

  /* ══ DEEP LINK ══ */

  function applyHashIfPresent(instance) {
    try {
      var hash = location.hash || '';
      var m = hash.match(/^#fabric=(.+)$/);
      if (!m) return;
      var code = decodeURIComponent(m[1]);
      var fabric = findFabricByCode(instance, code);
      if (fabric) openDetailDrawer(instance, fabric);
    } catch (e) { /* ignore */ }
  }

  /* ══ EVENTS ══ */

  function bindEvents(instance) {
    var root = instance.root;

    // Delegated click handler
    root.addEventListener('click', function(e) {
      var target = e.target;

      // Card click → open detail (unless click was on heart)
      var heart = closestEl(target, '[data-pfab-heart]');
      if (heart) {
        e.preventDefault();
        e.stopPropagation();
        var heartId = heart.getAttribute('data-fabric-id');
        if (heartId) toggleFavorite(instance, heartId);
        return;
      }

      var favToggle = closestEl(target, '[data-pfab-fav-toggle]');
      if (favToggle) {
        e.preventDefault();
        e.stopPropagation();
        var favId = favToggle.getAttribute('data-fabric-id');
        if (favId) toggleFavorite(instance, favId);
        return;
      }

      var favRemove = closestEl(target, '[data-fav-remove]');
      if (favRemove) {
        e.preventDefault();
        e.stopPropagation();
        var remId = favRemove.getAttribute('data-fabric-id');
        if (remId) toggleFavorite(instance, remId);
        return;
      }

      var favsItem = closestEl(target, '[data-pfab-favs-item]');
      if (favsItem && !closestEl(target, '[data-fav-remove]')) {
        var favsId = favsItem.getAttribute('data-fabric-id');
        var favsFab = findFabricById(instance, favsId);
        if (favsFab) {
          closeFavsDrawer(instance);
          openDetailDrawer(instance, favsFab);
        }
        return;
      }

      var card = closestEl(target, '[data-pfab-card]');
      if (card) {
        var cid = card.getAttribute('data-fabric-id');
        var fab = findFabricById(instance, cid);
        if (fab) openDetailDrawer(instance, fab);
        return;
      }

      // Color chip
      var colorBtn = closestEl(target, '[data-pfab-color]');
      if (colorBtn) {
        e.preventDefault();
        var ck = colorBtn.getAttribute('data-pfab-color');
        if (ck) setColor(instance, ck);
        return;
      }

      // Dropdown toggle
      var ddBtn = closestEl(target, '[data-pfab-dd-btn]');
      if (ddBtn) {
        e.preventDefault();
        e.stopPropagation();
        var ddType = ddBtn.getAttribute('data-pfab-dd-btn');
        toggleDropdown(instance, ddType);
        return;
      }

      // Filter option (dropdown or sheet)
      var filtBtn = closestEl(target, '[data-pfab-filter-type]');
      if (filtBtn) {
        e.preventDefault();
        if (filtBtn.getAttribute('disabled') !== null) return;
        var ft = filtBtn.getAttribute('data-pfab-filter-type');
        var fv = filtBtn.getAttribute('data-pfab-filter-value');
        setFilter(instance, ft, fv);
        closeAllDropdowns(instance);
        return;
      }

      // Active chip remove
      var removeBtn = closestEl(target, '[data-pfab-remove]');
      if (removeBtn) {
        e.preventDefault();
        removeFilter(instance, removeBtn.getAttribute('data-pfab-remove'));
        return;
      }

      // Clear all (header + inline)
      if (closestEl(target, '[data-pfab-clear]') || closestEl(target, '[data-pfab-clear-inline]')) {
        e.preventDefault();
        clearAllFilters(instance);
        return;
      }

      // Search clear button
      if (closestEl(target, '[data-pfab-search-clear]')) {
        e.preventDefault();
        if (instance.els.search) {
          instance.els.search.value = '';
          instance.els.search.focus();
        }
        setSearch(instance, '');
        return;
      }

      // Open a collection from the cards grid
      var openCol = closestEl(target, '[data-pfab-opencol]');
      if (openCol) {
        e.preventDefault();
        var colName = openCol.getAttribute('data-pfab-opencol');
        if (colName) {
          instance.state.activeCollection = colName;
          renderGrid(instance);
          renderActiveChips(instance);
          updateClearAllBtn(instance);
          renderCount(instance);
          // Scroll toolbar into view so user sees the "back" button
          var toolbar = root.querySelector('.pfab-toolbar');
          if (toolbar) {
            var y = toolbar.getBoundingClientRect().top + window.pageYOffset - 10;
            window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
          }
        }
        return;
      }

      // Back to collection cards
      var backBtn = closestEl(target, '[data-pfab-back-to-cols]');
      if (backBtn) {
        e.preventDefault();
        instance.state.activeCollection = null;
        renderGrid(instance);
        updateActiveChips(instance);
        updateClearAllBtn(instance);
        updateCountLabel(instance);
        return;
      }

      // Legacy collection toggle (kept for safety)
      var toggleCol = closestEl(target, '[data-pfab-toggle-col]');
      if (toggleCol) {
        e.preventDefault();
        var col = toggleCol.getAttribute('data-pfab-toggle-col');
        if (col) {
          instance.state.expandedCollections[col] = !instance.state.expandedCollections[col];
          renderGrid(instance);
        }
        return;
      }

      // Sheet controls
      if (closestEl(target, '[data-pfab-sheet-open]')) {
        e.preventDefault();
        openSheet(instance);
        return;
      }
      if (closestEl(target, '[data-pfab-sheet-close]')) {
        e.preventDefault();
        closeSheet(instance);
        return;
      }
      if (closestEl(target, '[data-pfab-sheet-apply]')) {
        e.preventDefault();
        closeSheet(instance);
        return;
      }
      if (closestEl(target, '[data-pfab-sheet-reset]')) {
        e.preventDefault();
        clearAllFilters(instance);
        return;
      }

      // Favorites toggle
      if (closestEl(target, '[data-pfab-favs-toggle]')) {
        e.preventDefault();
        openFavsDrawer(instance);
        return;
      }
      if (closestEl(target, '[data-pfab-favs-close]')) {
        e.preventDefault();
        closeFavsDrawer(instance);
        return;
      }
      if (closestEl(target, '[data-pfab-favs-clear]')) {
        e.preventDefault();
        if ((instance.state.favorites || []).length === 0) return;
        var ok = false;
        try { ok = window.confirm('Clear all favorites?'); } catch (e) { ok = true; }
        if (ok) {
          instance.state.favorites = [];
          saveFavorites([]);
          renderFavoritesDrawer(instance);
          updateFavsBadge(instance);
          // Unmark all hearts on page
          var allHearts = instance.root.querySelectorAll('.pfab-card__heart.is-active');
          for (var i = 0; i < allHearts.length; i++) {
            allHearts[i].classList.remove('is-active');
            allHearts[i].setAttribute('aria-pressed', 'false');
            var svg = allHearts[i].querySelector('svg');
            if (svg) svg.setAttribute('fill', 'none');
          }
        }
        return;
      }

      // Drawer close
      if (closestEl(target, '[data-pfab-drawer-close]')) {
        e.preventDefault();
        closeDetailDrawer(instance);
        return;
      }

      // Drawer backdrop close — clicking outside inner content
      if (instance.els.drawer && closestEl(target, '[data-pfab-drawer]') === instance.els.drawer) {
        if (!closestEl(target, '.pfab-drawer__panel, .pfab-drawer__content')) {
          // Was the target exactly the drawer root or a direct backdrop area?
          if (target === instance.els.drawer) {
            closeDetailDrawer(instance);
            return;
          }
        }
      }
    });

    // Search input — debounced
    if (instance.els.search) {
      instance.els.search.addEventListener('input', function(e) {
        var val = e.target.value;
        if (instance.searchTimer) clearTimeout(instance.searchTimer);
        instance.searchTimer = setTimeout(function() {
          setSearch(instance, val);
        }, SEARCH_DEBOUNCE_MS);
        // Immediately update the clear-button visibility
        if (val) {
          if (instance.els.searchClear) instance.els.searchClear.removeAttribute('hidden');
        } else {
          if (instance.els.searchClear) instance.els.searchClear.setAttribute('hidden', '');
        }
      });
      // Submit (Enter) bypasses debounce
      instance.els.search.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (instance.searchTimer) clearTimeout(instance.searchTimer);
          setSearch(instance, e.target.value);
        }
      });
    }

    // Close dropdowns on outside click (instance-scoped)
    document.addEventListener('click', function(e) {
      if (closestEl(e.target, '[data-pfab-dd]')) return;
      closeAllDropdowns(instance);
    });

    // Escape + focus trap
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (instance.els.drawer && instance.els.drawer.classList.contains('is-open')) {
          closeDetailDrawer(instance);
          return;
        }
        if (instance.els.favs && instance.els.favs.classList.contains('is-open')) {
          closeFavsDrawer(instance);
          return;
        }
        if (instance.els.sheet && instance.els.sheet.classList.contains('is-open')) {
          closeSheet(instance);
          return;
        }
        // Also close dropdowns
        closeAllDropdowns(instance);
      } else if (e.key === 'Tab' || e.keyCode === 9) {
        if (instance.els.drawer && instance.els.drawer.classList.contains('is-open')) {
          trapFocus(instance.els.drawer, e);
        } else if (instance.els.favs && instance.els.favs.classList.contains('is-open')) {
          trapFocus(instance.els.favs, e);
        } else if (instance.els.sheet && instance.els.sheet.classList.contains('is-open')) {
          trapFocus(instance.els.sheet, e);
        }
      }
    });

    // Hash change — deep link (user pastes URL)
    window.addEventListener('hashchange', function() {
      if (instance.allFabrics.length === 0) return;
      applyHashIfPresent(instance);
    });
  }

  function toggleDropdown(instance, type) {
    var menu = instance.root.querySelector('[data-pfab-dd-menu="' + type + '"]');
    var btn = instance.root.querySelector('[data-pfab-dd-btn="' + type + '"]');
    if (!menu || !btn) return;
    var willOpen = !menu.classList.contains('pfab-dd__menu--open');
    // Close all others first
    closeAllDropdowns(instance);
    if (willOpen) {
      menu.classList.add('pfab-dd__menu--open');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  function closeAllDropdowns(instance) {
    var menus = instance.root.querySelectorAll('.pfab-dd__menu--open');
    for (var i = 0; i < menus.length; i++) {
      menus[i].classList.remove('pfab-dd__menu--open');
    }
    var btns = instance.root.querySelectorAll('[data-pfab-dd-btn]');
    for (var j = 0; j < btns.length; j++) {
      btns[j].setAttribute('aria-expanded', 'false');
    }
  }

  /* ══ PUBLIC API ══ */

  var PoufudicFabrics = {
    init: function(root) {
      if (!root) return null;
      if (root.__pfabInstance) return root.__pfabInstance;
      var inst = createInstance(root);
      root.__pfabInstance = inst;
      return inst;
    },
    _createInstance: createInstance,
    _helpers: {
      cleanCollectionName: cleanCollectionName,
      cleanComposition: cleanComposition,
      getColorFromName: getColorFromName,
      getFabricCategory: getFabricCategory,
      getFabricFeatures: getFabricFeatures,
      escHtml: escHtml
    }
  };

  window.PoufudicFabrics = PoufudicFabrics;

  /* ══ AUTO-INIT ══ */

  function autoInit() {
    var roots = document.querySelectorAll('[data-pfab]');
    for (var i = 0; i < roots.length; i++) {
      PoufudicFabrics.init(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
